import { TransactionStoreService } from "./../../../services/store/transaction/transaction.store.service";
import {
  Component,
  computed,
  inject,
  OnInit,
  AfterViewInit,
  OnDestroy,
  signal,
  input,
  viewChild,
  ElementRef,
} from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { DatePipe } from "@angular/common";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./contribution-graph.component.scss";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";

// --- INTERFACES ---
interface TransactionView {
  postDate: Date | string;
  accountLink: {
    id: string;
  };
}

interface Square {
  level: number;
  dateString: string;
  transactionCount: number;
  transactionRangeString: string;
  dayOfWeek: number;
}

interface Monthlabel {
  name: string;
  columnStart: number;
}
// ------------------

@Component({
  selector: "app-contribution-graph",
  templateUrl: "./contribution-graph.component.html",
  providers: [DatePipe],
  host: {
    style: `--square-gap: 2px;
    --color-0: var(--color-border-subtle);
    --color-1: rgba(0, 196, 196, 0.18);
    --color-2: rgba(0, 196, 196, 0.34);
    --color-3: rgba(0, 196, 196, 0.5);
    --color-4: rgba(0, 196, 196, 0.75);
    --color-5: #00c4c4;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 12px;`,
    class: "contribution-graph-host",
  },
})
export class ContributionGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  // --- PROPERTIES ---
  accountId = input.required<string>();
  Math = Math;

  squaresData = signal<Square[]>([]);
  months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  scrollContainer = viewChild<ElementRef<HTMLDivElement>>("scrollContainer");
  scrollLeftButton = viewChild<ElementRef<HTMLButtonElement>>("scrollLeftButton");
  scrollRightButton = viewChild<ElementRef<HTMLButtonElement>>("scrollRightButton");
  canScrollLeft = signal(false);
  canScrollRight = signal(false);

  private resizeObserver?: ResizeObserver;
  private static readonly SCROLL_STEP_PX = 200;
  private static readonly WHEEL_EDGE_EPSILON_PX = 1;
  private static readonly WHEEL_DELTA_LINE_HEIGHT_PX = 16;
  // At 9px squares a centred tooltip in the Nth-from-last column stays inside the grid's 16px
  // padding only if it is narrower than 2 * (16 + 4.5 + 11 * (N - 1)) px: 107px for the 4th,
  // 129px for the 5th, 151px for the 6th. A "Sep 30, 2026 : 123" label is ~120px, so the first
  // centred column must be the 6th to leave headroom for larger counts.
  private static readonly END_ALIGNED_TOOLTIP_COLUMNS = 5;

  private transactionStoreService = inject(TransactionStoreService);
  private datePipe = inject(DatePipe);
  private deviceDetectorService: DeviceDetectorService;

  get isMobileDevice(): boolean {
    return this.deviceDetectorService.isMobile();
  }

  // --- NEW: Computed Signal for Date Range Parameters ---
  dateRange = computed(() => {
    const isMobile = this.isMobileDevice; // Reads the mobile status

    const endDate = new Date();
    const daysToShow = isMobile ? 183 : 365;
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - daysToShow);

    return { startDate, endDate, daysToShow, isMobile };
  });

  // --- NEW: Computed Signal for Month Labels ---
  // If monthLabels is used in the template, replace the old signal with this computed property
  monthLabels = computed(() => {
    const { startDate, daysToShow, isMobile } = this.dateRange();

    // Call the function to generate labels (no signal writing here)
    return this.generateMonthLabels(startDate, daysToShow, isMobile);
  });

  // Computed data logic (for the squares)
  data = computed(() => {
    const allTransactions = this.transactionStoreService.enrichTransaction.collection();
    const currentAccountId = this.accountId();
    const { startDate, daysToShow } = this.dateRange(); // Reads the date range signal

    if (!currentAccountId) {
      return [];
    }

    const filteredTransactions = allTransactions.filter((t) => {
      return t.accountLink.id === currentAccountId && !t.bankImportedBalance;
    });

    // Generate and return Square Data (no signal writing here)
    return this.generateContributionData(filteredTransactions, startDate, daysToShow);
  });

  // Index of the first square in the last few week columns. Those squares right-align their
  // tooltip: a centred tooltip there sticks out past the grid and inflates the scroller's
  // scrollWidth, so the arrow, fade and wheel takeover show even when the grid fits.
  endAlignedTooltipStartIndex = computed(() => {
    const squares = this.data();
    if (squares.length === 0) {
      return 0;
    }

    const firstDayOfWeek = squares[0].dayOfWeek;
    const lastColumn = Math.floor((squares.length - 1 + firstDayOfWeek) / 7);
    const firstEndAlignedColumn =
      lastColumn - ContributionGraphComponent.END_ALIGNED_TOOLTIP_COLUMNS + 1;
    return Math.max(0, firstEndAlignedColumn * 7 - firstDayOfWeek);
  });

  private generateMonthLabels(
    startDate: Date,
    daysToShow: number,
    isMobile: boolean,
  ): Monthlabel[] {
    const labels: Monthlabel[] = [];
    const date = new Date(startDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + daysToShow);

    let currentDayIndex = 0;
    let lastMonth = -1;

    // Loop through the dates to find the start of each month
    while (date.getTime() < endDate.getTime()) {
      const month = date.getMonth();
      if (month !== lastMonth) {
        const columnStart = Math.floor(currentDayIndex / 7) + 1;

        labels.push({
          name: this.months[month],
          columnStart: columnStart,
        });
        lastMonth = month;
      }

      date.setDate(date.getDate() + 1);
      currentDayIndex++;
    }

    // Ensures only the most recent 12 months show up
    const limit = isMobile ? 6 : 12;
    return labels.slice(-limit);
  }

  // Injecting DeviceDetectorService here, matching your example file
  constructor(private deviceService: DeviceDetectorService) {
    this.deviceDetectorService = this.deviceService;
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.updateScrollState();
    this.resizeObserver = new ResizeObserver(() => this.updateScrollState());
    this.resizeObserver.observe(this.scrollContainer()!.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  scrollByStep(direction: 1 | -1): void {
    this.scrollContainer()!.nativeElement.scrollBy({
      left: direction * ContributionGraphComponent.SCROLL_STEP_PX,
      behavior: "smooth",
    });
  }

  onScroll(): void {
    this.updateScrollState();
  }

  onWheel(event: WheelEvent): void {
    const el = this.scrollContainer()!.nativeElement;

    if (el.scrollWidth <= el.clientWidth + 1) {
      return;
    }

    // Pinch-zoom and Ctrl+wheel zoom arrive as ctrlKey wheel events; leave them to the browser.
    if (event.ctrlKey) {
      return;
    }

    // Trackpad swipes / Shift+wheel are already horizontal — leave them alone.
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
      return;
    }

    let delta = event.deltaY;
    if (event.deltaMode === 1) {
      delta *= ContributionGraphComponent.WHEEL_DELTA_LINE_HEIGHT_PX; // line mode (Firefox)
    } else if (event.deltaMode === 2) {
      delta *= el.clientWidth; // page mode
    }

    // Edge release: don't trap the wheel once the grid is fully scrolled either way.
    const max = el.scrollWidth - el.clientWidth;
    const blockedLeft =
      delta < 0 && el.scrollLeft <= ContributionGraphComponent.WHEEL_EDGE_EPSILON_PX;
    const blockedRight =
      delta > 0 && el.scrollLeft >= max - ContributionGraphComponent.WHEEL_EDGE_EPSILON_PX;
    if (blockedLeft || blockedRight) {
      return;
    }

    event.preventDefault();
    el.scrollLeft += delta;
  }

  private updateScrollState(): void {
    const el = this.scrollContainer()!.nativeElement;
    const canLeft = el.scrollLeft > 0;
    const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;

    // An arrow that reaches the edge is removed from the DOM; if it had focus, hand focus to
    // the scroller so keyboard users aren't dropped back to <body>.
    const active = document.activeElement;
    const losingFocusedArrow =
      (!canLeft && active === this.scrollLeftButton()?.nativeElement) ||
      (!canRight && active === this.scrollRightButton()?.nativeElement);
    if (losingFocusedArrow) {
      el.focus({ preventScroll: true });
    }

    this.canScrollLeft.set(canLeft);
    this.canScrollRight.set(canRight);
  }

  private generateContributionData(
    transactions: TransactionView[],
    startDate: Date,
    daysToShow: number,
  ) {
    const data: Square[] = [];
    const dailyCounts = this.getDailyCounts(transactions);

    const countsArray = Object.values(dailyCounts);
    const maxDailyTransaction = countsArray.length > 0 ? Math.max(...countsArray) : 0;

    const contributionLevels = [
      { max: 0, level: 0, range: "level 0" },
      { max: maxDailyTransaction * 0.2, level: 1, range: "level 1" },
      { max: maxDailyTransaction * 0.4, level: 2, range: "level 2" },
      { max: maxDailyTransaction * 0.6, level: 3, range: "level 3" },
      { max: maxDailyTransaction * 0.8, level: 4, range: "level 4" },
      { max: Infinity, level: 5, range: "level 5" },
    ];

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + daysToShow);

    const dateOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };

    const loopDate = new Date(startDate);
    const endDateTimeStamp = endDate.getTime();

    while (loopDate.getTime() <= endDateTimeStamp) {
      const dateKey = this.datePipe.transform(loopDate, "yyyy-MM-dd") as string;

      const transactionCount = dailyCounts[dateKey] || 0;
      let level = 0;
      let rangeString = "No transactions";

      for (const threshold of contributionLevels) {
        if (transactionCount <= threshold.max) {
          level = threshold.level;
          rangeString = threshold.range;
          break;
        }
      }

      const dayOfWeek = loopDate.getDay();

      data.push({
        level,
        dateString: loopDate.toLocaleDateString("en-US", dateOptions),
        transactionCount,
        transactionRangeString: rangeString,
        dayOfWeek: dayOfWeek,
      });

      loopDate.setDate(loopDate.getDate() + 1);
    }
    return data;
  }

  private getDailyCounts(transactions: TransactionView[]): Record<string, number> {
    const counts: Record<string, number> = {};

    let isoDateString: string;

    for (const t of transactions) {
      if (t.postDate instanceof Date) {
        isoDateString = t.postDate.toISOString();
      } else {
        isoDateString = t.postDate;
      }

      const dateKey = GlossDateFactory.createFromISOString(isoDateString, true).dateString;
      counts[dateKey] = (counts[dateKey] || 0) + 1;
    }
    return counts;
  }
}
